import { ValueFormatterParams } from "ag-grid-community";
import React, { useCallback, useMemo, useRef, useState } from "react";
import PlusButtonIcon from "../../../../assets/images/PlusButtonIcon.svg";
import Grid from "../../../../components/Grid";
import { ColumnDef, GridHandle } from "../../../../components/Grid/model/Grid";
import ModalDelete from "../../../../components/ModalDelete";
import Loading from "../../../../core/common/Loading";
import { useModal } from "../../../../hooks/ModalContext";
import api from "../../../../services/api";
import Create from "./Create";
import { IUsuario } from "./Create/types/types";
import Info from "./Info";
import Can from "../../../../components/Can";

interface IGrupoUsuario {
  subsistema?: string;
  grupos?: {
    nome?: string;
    subsistema?: string;
  };
}

interface IUsuarioListagem extends IUsuario {
  grupos_usuarios?: IGrupoUsuario[] | IGrupoUsuario;
}

interface IValueGetterParams {
  data: IUsuarioListagem;
}

const ListUsuario: React.FC = () => {

  const getGruposUsuarios = (data?: IUsuarioListagem): IGrupoUsuario[] => {
    if (Array.isArray(data?.grupos_usuarios)) {
      return data.grupos_usuarios;
    }

    return data?.grupos_usuarios ? [data.grupos_usuarios] : [];
  };

  const getUniqueValues = (values: Array<string | undefined>) => {
    return [...new Set(values.filter(Boolean))].join(", ") || "---";
  };

  const columns = useMemo<ColumnDef[]>(() => [
    {
      field: "grupos_usuarios.subsistema",
      headerName: "Subsistemas",
      flex: 1,
      filter: true,
      sortable: true,
      fieldName: "grupos_usuarios.subsistema",
      valueGetter: (params: IValueGetterParams) => {
        const gruposUsuarios = getGruposUsuarios(params.data);
        return getUniqueValues(
          gruposUsuarios.map((groupUser) => groupUser.subsistema || groupUser.grupos?.subsistema)
        );
      }
    },
    {
      field: "nome",
      headerName: "Nome",
      flex: 1,
      filter: true,
      sortable: true,
    },
    {
      field: "cpf",
      headerName: "CPF",
      flex: 1,
      filter: true,
      sortable: true,
    },
    {
      field: "email",
      headerName: "E-mail",
      flex: 1,
      filter: true,
      sortable: true,
    },
    {
      field: "grupos", 
      headerName: "Grupos",
      flex: 1,
      filter: true,
      sortable: true,
      fieldName: "grupos_usuarios.grupos.nome",
      valueGetter: (params: IValueGetterParams) => {
        const gruposUsuarios = getGruposUsuarios(params.data);
        return getUniqueValues(
          gruposUsuarios.map((groupUser) => groupUser.grupos?.nome)
        );
      }
    },
    {
      field: "usuario",
      headerName: "Usuário",
      filter: true,
      valueFormatter: (params: ValueFormatterParams) => {
        if (params.value) {
          return params.value;
        }
        return "---";
      },
    },
  ], []);

  const [isRemove, setIsRemove] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedRow, setSelectedRows] = useState<IUsuario>();
  const [isView, setIsView] = useState<boolean>(false);
  const [isEdit, setIsEdit] = useState<boolean>(false);

  const gridRef = useRef<GridHandle>(null);

  const { isModalOpen, closeModal, openModal } = useModal();

  const onDelete = useCallback(async (rowId?: number) => {
    try {
      setLoading(true);
      const body = {
        id_configuracao_usuario: rowId,
      };

      await api.post("/deletar/usuarios", body);

      setLoading(false);

      setIsRemove(false);

      gridRef.current?.refresh();
    } catch {
      setLoading(false);
    }
  }, []);

  return (
    <>
      {isModalOpen && (
        <Create
          isView={isView}
          isEdit={isEdit}
          selectedRow={selectedRow}
          onClear={() => {
            closeModal();
            setIsEdit(false);
          }}
          onConfirm={() => {
            closeModal();
            gridRef.current?.refresh();
          }}
        />
      )}

      <Loading loading={loading} />

      {isRemove && (
        <ModalDelete
          onCancel={() => setIsRemove(!isRemove)}
          onConfirm={() => onDelete(selectedRow?.id_usuario)}
          row={`Usuário - ${selectedRow?.nome}`}
        />
      )}
      {isView && (
        <Info
          data={selectedRow}
          title="Conhecer - Usuário"
          onClose={() => setIsView(!isView)}
        />
      )}

      <div className="flex flex-col w-full h-screen bg-[#F5F5F5] p-5">
        <div className="flex items-center justify-between w-full mb-3">
          <div>
            <h1 className="text-2xl text-[#000000] font-bold">
              Usuários
            </h1>
          </div>
          <div className="mr-14">
            <Can acao="SALVAR">
            <button
              className="flex items-center justify-center h-12 w-36 bg-[#062D4E] text-[#FFFFFF] text-sm font-light border-none rounded-full"
              onClick={() => openModal()}
              id="btnCreate"
            >
              Adicionar <img src={PlusButtonIcon} alt="" className="ml-2" />
            </button>
            </Can>
          </div>
        </div>
        <div className="flex w-full">
          <Grid
            ref={gridRef}
            columns={columns}
            filters={[]}
            pagination
            perPage={10}
            path="/listar/usuarios"
            status={[]}
            defaultSortField="id_usuario"
            onDelete={(data: IUsuario) => {
              setIsRemove(!isRemove);
              setSelectedRows(data);
            }}
            onView={(data: IUsuario) => {
              setSelectedRows(data);
              setIsView(!isView);
            }}
            onUpdate={(data: IUsuario) => {
              setSelectedRows(data);
              setIsEdit(!isEdit);
              openModal();
            }}
          />
        </div>
      </div>
    </>
  );
};

export default ListUsuario;

